import {
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
} from "@floating-ui/react";
import { RowSelectionState } from "@tanstack/react-table";
import { useState } from "react";

import { Button } from "../button";
import { Dropdown } from "../dropdown";

type BulkActionsButtonProps = {
  selectedRows: RowSelectionState[];
  batchActions: {
    text: string;
    onClick: (selected: any[]) => Promise<void> | void;
    className?: string;
  }[];
  loading: boolean;
  setLoading: (loading: boolean) => void;
};

export const BulkActionsButton = ({
  selectedRows,
  batchActions,
  setLoading,
  loading,
}: BulkActionsButtonProps) => {
  const [open, setOpen] = useState(false);

  const floating = useFloating({
    placement: "bottom-start",
    open,
    onOpenChange: setOpen,
    middleware: [
      offset({ mainAxis: 4, crossAxis: -4 }),
      flip({ fallbackPlacements: ["top-start"] }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const interactions = useInteractions([
    useDismiss(floating.context),
    useFocus(floating.context),
  ]);

  return (
    <div className="flex justify-center min-w-[16rem]">
      <div
        ref={floating.refs.setReference}
        {...interactions.getReferenceProps()}
      >
        <Button
          variant="primarySubtle"
          size="small"
          text={
            selectedRows.length
              ? `Bulk Actions (${selectedRows.length})`
              : "Bulk Actions"
          }
          disabled={!selectedRows.length}
          loading={loading}
          onClick={() => setOpen(!open)}
        />
      </div>
      {open && (
        <div
          className="z-modal"
          ref={floating.refs.setFloating}
          style={floating.floatingStyles}
          {...interactions.getFloatingProps()}
        >
          <Dropdown
            items={batchActions.map((action) => ({
              label: `${action.text} (${selectedRows.length})`,
              onClick: async () => {
                setLoading(true);
                setOpen(false);
                await action.onClick(selectedRows);
                setLoading(false);
              },
              className: action.className,
            }))}
          />
        </div>
      )}
    </div>
  );
};
